/**
 * Query key factory for consistent TanStack Query cache management.
 *
 * Provides a hierarchical key structure: scope → resource → filters.
 * Using the factory ensures cache invalidation works correctly across
 * the app (e.g., invalidating all "seo" keys or just "seo.keywords").
 */

/**
 * Create a scoped query key factory for a domain.
 *
 * @example
 * ```ts
 * const seoKeys = createQueryKeys('seo');
 *
 * // List with filters
 * seoKeys.list('keywords', { siteId: '123', page: 1 })
 * // → ['seo', 'keywords', 'list', { siteId: '123', page: 1 }]
 *
 * // Single resource
 * seoKeys.detail('keywords', '456')
 * // → ['seo', 'keywords', 'detail', '456']
 *
 * // Invalidate all keywords
 * queryClient.invalidateQueries({ queryKey: seoKeys.resource('keywords') })
 * // → invalidates ['seo', 'keywords', ...]
 *
 * // Invalidate everything in scope
 * queryClient.invalidateQueries({ queryKey: seoKeys.all })
 * // → invalidates ['seo', ...]
 * ```
 */
export function createQueryKeys(scope: string) {
  return {
    /** Top-level key — invalidates everything in this scope */
    all: [scope] as const,

    /** Resource-level key — invalidates all queries for a resource */
    resource: (resource: string) => [scope, resource] as const,

    /** List key with optional filters */
    list: (resource: string, filters?: Record<string, unknown>) =>
      filters ? ([scope, resource, "list", filters] as const) : ([scope, resource, "list"] as const),

    /** Detail key for a single entity */
    detail: (resource: string, id: string | number) => [scope, resource, "detail", id] as const,
  };
}
